// src/services/api.ts
import { handleApiError } from '../utils/errorHandler';
import SessionDB from '../utils/sessionStorage';
import { API_ENDPOINTS, APIHeaders } from '../globalConstants';
import { emitToast } from '../state/ToastContext';

type AuthSync = (accessToken: string | null) => void;
let authSync: AuthSync | null = null;

/**
 * Register the store/component that keeps the redux token in sync whenever
 * the API client refreshes (or clears) credentials underneath a request.
 */
export function setAuthSync(cb: AuthSync): void {
    authSync = cb;
}

/**
 * Endpoints that must never trigger an automatic token refresh, otherwise
 * a bad password (login) or a dead session (logout) would loop/refresh.
 */
const NO_RETRY_URLS = new Set<string>([
    API_ENDPOINTS.auth.LOGIN,
    API_ENDPOINTS.auth.REFRESH_TOKEN,
    API_ENDPOINTS.auth.LOGOUT,
]);

export type RefreshOutcome = 'ok' | 'invalid' | 'network';

let refreshing: Promise<RefreshOutcome> | null = null;

/** Friendly resource name derived from the request URL for toast copy. */
function resourceLabel(url: string): string {
    const u = url.toLowerCase();
    if (u.includes('/fund-transfers')) return 'Transfer';
    if (u.includes('/sales')) return 'Sale';
    if (u.includes('/credits') || u.includes('/settle')) return 'Credit';
    if (u.includes('/due-collections')) return 'Due collection';
    if (u.includes('/products')) return 'Product';
    if (u.includes('/services')) return 'Service';
    if (u.includes('/print-materials') || u.includes('/materials')) return 'Material';
    if (u.includes('/customers')) return 'Customer';
    if (u.includes('/vendors')) return 'Vendor';
    if (u.includes('/purchases')) return 'Purchase';
    if (u.includes('/expenses')) return 'Expense';
    if (u.includes('/payment')) return 'Payment';
    if (u.includes('/staff') || u.includes('/team') || u.includes('/member')) return 'Team member';
    if (u.includes('/tasks')) return 'Task';
    if (u.includes('/shops')) return 'Shop';
    if (u.includes('/reports')) return 'Report';
    if (u.includes('/settings')) return 'Settings';
    if (u.includes('/logout')) return 'Logout';
    if (u.includes('/login') || u.includes('/auth')) return 'Login';
    return 'Request';
}

function shortMessage(raw: unknown, fallback: string): string {
    if (typeof raw !== 'string' || !raw.trim()) return fallback;
    // Server errors look like "HTTP 400: Bad Request - {...json...}".
    // Prefer the tail of the message and keep toasts readable.
    const tail = raw.split(' - ').pop() ?? raw;
    const cleaned = tail.replace(/^HTTP \d+:\s*/i, '').trim();
    if (!cleaned) return fallback;
    return cleaned.length > 140 ? `${cleaned.slice(0, 137)}…` : cleaned;
}

/**
 * Swap the expired access token for a fresh pair using the saved refresh
 * token. `network` means the refresh call itself failed to reach the server
 * (keep the session), `invalid` means the refresh token was rejected (the
 * session is genuinely dead and the client should sign out).
 */
function performTokenRefresh(): Promise<RefreshOutcome> {
    const refreshToken = SessionDB.getRefreshToken();
    if (!refreshToken) return Promise.resolve('invalid');

    return fetch(API_ENDPOINTS.auth.REFRESH_TOKEN, {
        method: 'POST',
        headers: APIHeaders,
        body: JSON.stringify({ refreshToken }),
    })
        .then(async (response) => {
            if (!response.ok) {
                return response.status === 400 || response.status === 401
                    ? 'invalid'
                    : 'network';
            }
            const json = await response.json();
            const accessToken: unknown = json?.data?.accessToken;
            const nextRefresh: unknown = json?.data?.refreshToken;
            if (typeof accessToken !== 'string') return 'invalid';
            SessionDB.setAccessToken(accessToken);
            if (typeof nextRefresh === 'string') {
                SessionDB.setRefreshToken(nextRefresh);
            }
            authSync?.(accessToken);
            return 'ok';
        })
        .catch(() => 'network');
}

/**
 * Generic API client for fetch-based requests.
 * - Accepts full URL (no baseURL logic inside the client)
 * - Allows passing custom headers and options from saga
 * - Transparently refreshes an expired access token and retries once
 */
class ApiClient {
    constructor() {
        this.request = this.request.bind(this);
        this.get = this.get.bind(this);
        this.post = this.post.bind(this);
        this.put = this.put.bind(this);
        this.patch = this.patch.bind(this);
        this.delete = this.delete.bind(this);
    }

    async request<T>(
        url: string,
        options: RequestInit = {},
        timeout: number = 10000,
    ): Promise<T> {
        const opts = options as RequestInit & {
            __retried?: boolean;
            /** Skip the error toast when the failure is a 403 (expected authz state). */
            __silentForbidden?: boolean;
            /** Skip all auto-toasts; the caller emits its own notification. */
            __silentToast?: boolean;
        };
        const method = (options.method || 'GET').toUpperCase();
        const isMutation = method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
        // Auth endpoints have their own dedicated messaging — don't double-toast.
        const isSilentUrl = NO_RETRY_URLS.has(url);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                if (
                    response.status === 401 &&
                    !NO_RETRY_URLS.has(url) &&
                    !opts.__retried
                ) {
                    if (!refreshing) {
                        refreshing = performTokenRefresh().finally(() => {
                            refreshing = null;
                        });
                    }
                    const outcome = await refreshing;
                    if (outcome === 'ok') {
                        const token = SessionDB.getAccessToken();
                        const retryHeaders = {
                            ...(options.headers || {}),
                            Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                        } as Record<string, string>;
                        const retryOpts: any = {
                            ...options,
                            headers: retryHeaders,
                            __retried: true,
                        };
                        return this.request<T>(url, retryOpts, timeout);
                    }
                    if (outcome === 'invalid') {
                        // Refresh token rejected — the session is finished.
                        SessionDB.setAccessToken(null);
                        SessionDB.setRefreshToken(null);
                        authSync?.(null);
                    }
                    // outcome === 'network': leave the session alone and
                    // surface the original 401 below.
                }
                // Try to parse error body if possible
                let errorBody = '';
                try {
                    errorBody = await response.text();
                } catch {}
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}${
                        errorBody ? ' - ' + errorBody : ''
                    }`
                );
            }
            // Try to parse JSON, fallback to text
            let result: T;
            try {
                result = await response.json();
            } catch {
                result = (await response.text()) as any;
            }
            if (isMutation && !isSilentUrl && !opts.__silentToast) {
                const label = resourceLabel(url);
                const verb =
                    method === 'DELETE' ? 'deleted' : method === 'POST' ? 'saved' : 'updated';
                emitToast({
                    message: `${label} ${verb} successfully`,
                    tone: 'success',
                });
            }
            return result;
        } catch (error) {
            const label = resourceLabel(url);
            const status =
                typeof (error as any)?.status === 'number'
                    ? (error as any).status
                    : /HTTP (\d+)/.exec(error instanceof Error ? error.message : String(error))?.[1];
            const silentForbidden = opts.__silentForbidden === true && String(status) === '403';
            if (error instanceof Error && error.name === 'AbortError') {
                if (!opts.__silentToast) {
                    emitToast({
                        message: `${label} request timed out`,
                        description: 'The server took too long. Please try again.',
                        tone: 'error',
                    });
                }
            } else if (!isSilentUrl && !silentForbidden && !opts.__silentToast) {
                const verb =
                    method === 'DELETE' ? 'delete' : method === 'GET' ? 'load' : 'save';
                emitToast({
                    message: `Couldn't ${verb} ${label.toLowerCase()}`,
                    description: shortMessage(
                        error instanceof Error ? error.message : String(error),
                        'Please check your connection and try again.',
                    ),
                    tone: 'error',
                });
            }
            handleApiError(error);
            throw error;
        }
    }

    get<T>(url: string, options: RequestInit = {}, timeout?: number): Promise<T> {
        return this.request<T>(url, { ...options, method: 'GET' }, timeout);
    }

    post<T>(
        url: string,
        data: any,
        options: RequestInit = {},
        timeout?: number
    ): Promise<T> {
        return this.request<T>(
            url,  
            {
                ...options,
                method: 'POST',
                body: JSON.stringify(data),
            },
            timeout
        );
    }

    put<T>(
        url: string,
        data: any,
        options: RequestInit = {},
        timeout?: number
    ): Promise<T> {
        return this.request<T>(
            url,
            {
                ...options,
                method: 'PUT',
                body: JSON.stringify(data),
            },
            timeout
        );
    }

    patch<T>(
        url: string,
        data: any,
        options: RequestInit = {},
        timeout?: number
    ): Promise<T> {
        return this.request<T>(
            url,
            {
                ...options,
                method: 'PATCH',
                body: JSON.stringify(data),
            },
            timeout
        );
    }

    delete<T>(url: string, options: RequestInit = {}, timeout?: number): Promise<T> {
        return this.request<T>(url, { ...options, method: 'DELETE' }, timeout);
    }
}

export const apiClient = new ApiClient();
export * from '../globalConstants';